import React, { Component } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, Modal, AppState, ActivityIndicator } from 'react-native';
import { Camera } from 'expo-camera';
import { BarcodeFormat, BrowserMultiFormatReader, HTMLCanvasElementLuminanceSource, BinaryBitmap, HybridBinarizer } from '@zxing/library';
import CameraControls from './CameraControls';
import ScanArea from './ScanArea';
import ZoomSlider from './ZoomSlider';
import PermissionModal from './modals/PermissionModal';
import CameraSelectModal from './modals/CameraSelectModal';
import HelperModal from './modals/HelperModal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';

const FORBIDDEN_FORMATS = new Set([
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.AZTEC,
  BarcodeFormat.PDF_417,
  BarcodeFormat.MAXICODE,
]);

const SCAN_TIMEOUT_MS = 10000;

let _helperShownThisSession = false;
let _permissionKnownGranted = false;

function _clearPermissionFlag() {
  _permissionKnownGranted = false;
  AsyncStorage.removeItem('cameraPermissionHint').catch(() => {});
}

let _cachedStream = null;
let _cachedStreamTimer = null;

function _cacheStream(stream) {
  if (_cachedStreamTimer) {
    clearTimeout(_cachedStreamTimer);
    _cachedStreamTimer = null;
  }
  _cachedStream = stream;
  _cachedStreamTimer = setTimeout(() => {
    if (_cachedStream) {
      _cachedStream.getTracks().forEach(t => t.stop());
      _cachedStream = null;
    }
    _cachedStreamTimer = null;
  }, 5000);
}

function _takeCachedStream() {
  if (_cachedStreamTimer) {
    clearTimeout(_cachedStreamTimer);
    _cachedStreamTimer = null;
  }
  const stream = _cachedStream;
  _cachedStream = null;
  if (stream) {
    const track = stream.getVideoTracks()[0];
    if (track && track.readyState === 'live' && !track.muted) {
      return stream;
    }
    stream.getTracks().forEach(t => { try { t.stop(); } catch (e) {} });
  }
  return null;
}

class CamScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasPermission: null,
      showPermissionModal: false,
      showCameraSelectModal: false,
      cameraSelectStep: 2,
      cameraSelectShowBack: false,
      isSwitchCameraMode: false,
      showHelperModal: false,
      helperModalType: null,
      helperOpenedFromInfo: false,
      cameras: [],
      selectedCameraId: null,
      isScanning: false,
      zoom: 1,
      torchEnabled: false,
      cameraType: Camera.Constants.Type.back,
      hasTorch: false,
      isCameraReady: false,
      needsManualRestart: false,
      toastMessage: null,
    };

    this.cameraRef = React.createRef();
    this.canvasRef = React.createRef();
    this.scanIntervalRef = null;
    this.scanStartTimeRef = null;
    this.readerRef = null;
    this.appStateSubscription = null;
    this.permissionCheckInterval = null;
    this.permissionStatusRef = null;
    this.handleVisibilityChange = null;
    this.handleFocusChange = null;
    this.trackStateInterval = null;
    this._unmounting = false;
    this.videoTrackRef = null;
    this._wasBackgroundedWithCamera = false;
    this._restartingAfterBackground = false;
  }

  async componentDidMount() {
    await this.initializeCamera();
    this.appStateSubscription = AppState.addEventListener('change', this.handleAppStateChange);
    this.startPermissionMonitoring();
  }

  componentWillUnmount() {
    this._unmounting = true;
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this.cleanup();

    if (Platform.OS === 'web' && this.videoTrackRef) {
      this.videoTrackRef.onended = null;
      this.videoTrackRef.onmute = null;
      if (this.videoTrackRef.readyState === 'live' && !this.videoTrackRef.muted) {
        const video = this.cameraRef.current;
        if (video && video.srcObject) {
          _cacheStream(video.srcObject);
        } else {
          try { this.videoTrackRef.stop(); } catch (e) {}
        }
      } else {
        try { this.videoTrackRef.stop(); } catch (e) {}
      }
      this.videoTrackRef = null;
    }

    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
    }

    this.stopPermissionMonitoring();
  }

  cleanup = () => {
    if (this.scanIntervalRef) {
      clearInterval(this.scanIntervalRef);
      this.scanIntervalRef = null;
    }
    if (this.readerRef && Platform.OS === 'web') {
      this.readerRef.reset();
      this.readerRef = null;
    }
  };

  handleAppStateChange = async (nextAppState) => {
    if (this._restartingAfterBackground || this._wasBackgroundedWithCamera) return;
    if (nextAppState === 'active') {
      if (this.videoTrackRef &&
          this.videoTrackRef.readyState === 'live' &&
          !this.videoTrackRef.muted) {
        console.log('[CamScanner] AppState active: camera track already alive, skipping reinitialize');
        return;
      }
      const cameraStateStr = await AsyncStorage.getItem('cameraState');
      if (cameraStateStr) {
        const cameraState = JSON.parse(cameraStateStr);
        if (cameraState.isOpen && this.state.hasPermission) {
          await this.initializeCamera();
        }
      }
    }
  };

  startPermissionMonitoring = () => {
    if (Platform.OS === 'web') {
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'camera' }).then(permissionStatus => {
          this.permissionStatusRef = permissionStatus;
          permissionStatus.onchange = () => {
            console.log('[CamScanner] Web camera permission changed:', permissionStatus.state);
            if ((permissionStatus.state === 'denied' || permissionStatus.state === 'prompt') && this.state.hasPermission === true) {
              console.log('[CamScanner] Camera permission revoked on web, closing scanner');
              _clearPermissionFlag();
              if (this.videoTrackRef) {
                this.videoTrackRef.onended = null;
                this.videoTrackRef.onmute = null;
                this.videoTrackRef.stop();
                this.videoTrackRef = null;
              }
              if (this.props.onClose) {
                this.props.onClose();
              }
            }
          };
        }).catch(err => {
          console.warn('[CamScanner] Permissions API not available:', err);
        });
      }

      this.handleVisibilityChange = async () => {
        if (document.visibilityState !== 'visible') {
          if (this.state.hasPermission === true && this.videoTrackRef) {
            this._wasBackgroundedWithCamera = true;
          }
          return;
        }

        if (this.state.hasPermission !== true || this._unmounting) return;

        const wasBackgrounded = this._wasBackgroundedWithCamera;
        this._wasBackgroundedWithCamera = false;

        if (this.videoTrackRef &&
            this.videoTrackRef.readyState === 'live' &&
            !this.videoTrackRef.muted) {
          if (this.cameraRef.current && this.cameraRef.current.paused) {
            console.log('[CamScanner] Track alive but video paused — resuming playback');
            try { await this.cameraRef.current.play(); } catch (e) {}
          }
          return;
        }

        if (wasBackgrounded) {
          if (this._restartingAfterBackground) return;
          this._restartingAfterBackground = true;
          console.log('[CamScanner] Returning from background — restarting camera');

          if (this.videoTrackRef) {
            this.videoTrackRef.onended = null;
            this.videoTrackRef.onmute = null;
            try { this.videoTrackRef.stop(); } catch (e) {}
            this.videoTrackRef = null;
          }

          if (this._unmounting) { this._restartingAfterBackground = false; return; }

          let permissionConfirmed = false;

          if (navigator.permissions && navigator.permissions.query) {
            try {
              const permStatus = await navigator.permissions.query({ name: 'camera' });
              if (this._unmounting) { this._restartingAfterBackground = false; return; }
              if (permStatus.state === 'denied') {
                console.log('[CamScanner] Permission denied after background — closing');
                this._restartingAfterBackground = false;
                _clearPermissionFlag();
                if (this.props.onClose) this.props.onClose();
                return;
              }
              if (permStatus.state === 'granted') {
                permissionConfirmed = true;
                console.log('[CamScanner] Background return: Permissions API confirms granted');
              }
            } catch (e) {
              console.log('[CamScanner] Background return: Permissions API unavailable for camera');
            }
          }

          if (this._unmounting) { this._restartingAfterBackground = false; return; }

          if (!permissionConfirmed) {
            try {
              const devices = await navigator.mediaDevices.enumerateDevices();
              const hasLabels = devices.some(d => d.kind === 'videoinput' && d.label);
              if (hasLabels) {
                permissionConfirmed = true;
                console.log('[CamScanner] Background return: enumerateDevices labels confirm granted');
              }
            } catch (e) { /* ignore */ }
          }

          if (this._unmounting) { this._restartingAfterBackground = false; return; }

          if (!permissionConfirmed) {
            console.log('[CamScanner] Background return: cannot confirm permission — closing to avoid dialog');
            this._restartingAfterBackground = false;
            if (this.props.onClose) this.props.onClose();
            return;
          }

          const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                        (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);
          if (isIOSDevice) {
            console.log('[CamScanner] iOS: skipping auto-getUserMedia — showing tap-to-restart');
            this._restartingAfterBackground = false;
            this.setState({ needsManualRestart: true, isCameraReady: false });
            return;
          }

          try {
            this.setState({ isCameraReady: false });
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { ideal: 'environment' } }
            });
            if (this._unmounting) {
              stream.getTracks().forEach(t => t.stop());
              this._restartingAfterBackground = false;
              return;
            }
            await this.startVideoStream('', stream);
            _permissionKnownGranted = true;
            AsyncStorage.setItem('cameraPermissionHint', 'granted').catch(() => {});
            this._restartingAfterBackground = false;
            console.log('[CamScanner] Camera restarted after background (silent getUserMedia)');
          } catch (err) {
            this._restartingAfterBackground = false;
            if (this._unmounting) return;
            console.log('[CamScanner] Restart after background failed:', err.name, '— closing');
            _clearPermissionFlag();
            if (this.props.onClose) this.props.onClose();
          }
          return;
        }

        if (this.videoTrackRef && this.videoTrackRef.readyState === 'ended') {
          console.log('[CamScanner] Video track ended on visibility restore — permission revoked');
          _clearPermissionFlag();
          if (this.props.onClose) this.props.onClose();
          return;
        }

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                      (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);

        if (isIOS) {
          if (this.videoTrackRef) {
            this.videoTrackRef.onended = null;
            this.videoTrackRef.onmute = null;
            try { this.videoTrackRef.stop(); } catch (e) {}
            this.videoTrackRef = null;
          }

          if (this._unmounting) return;

          await new Promise(resolve => setTimeout(resolve, 300));
          if (this._unmounting || this.state.hasPermission !== true) return;

          let permissionState = null;
          if (navigator.permissions && navigator.permissions.query) {
            try {
              const permStatus = await navigator.permissions.query({ name: 'camera' });
              permissionState = permStatus.state;
            } catch (e) {}
          }

          if (this._unmounting) return;

          if (permissionState === 'denied' || permissionState === 'prompt') {
            console.log('[CamScanner] iOS: camera permission', permissionState, '— closing scanner');
            _clearPermissionFlag();
            if (this.props.onClose) this.props.onClose();
            return;
          }

          try {
            const testStream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { ideal: 'environment' } }
            });
            if (this._unmounting) {
              testStream.getTracks().forEach(t => t.stop());
              return;
            }
            this.setState({ isCameraReady: false });
            await this.startVideoStream('', testStream);
          } catch (err) {
            if (!this._unmounting) {
              console.log('[CamScanner] iOS: getUserMedia probe failed:', err.name, '— closing scanner');
              _clearPermissionFlag();
              if (this.props.onClose) this.props.onClose();
            }
            return;
          }

          if (!this._unmounting && !this.videoTrackRef && this.state.hasPermission === true) {
            console.log('[CamScanner] iOS: stream restart failed silently — closing scanner');
            if (this.props.onClose) this.props.onClose();
          }
          return;
        }

        if (this.videoTrackRef) {
          try {
            const testStream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { ideal: 'environment' } }
            });
            testStream.getTracks().forEach(t => t.stop());
          } catch (err) {
            if (
              (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') &&
              !this._unmounting &&
              this.state.hasPermission === true
            ) {
              console.log('[CamScanner] Permission revoked — confirmed via getUserMedia probe on visibility restore');
              _clearPermissionFlag();
              if (this.props.onClose) this.props.onClose();
            }
          }
        }
      };
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.handleFocusChange = () => {
        if (this._unmounting || this._restartingAfterBackground || this._wasBackgroundedWithCamera || this.state.hasPermission !== true) return;
        if (this.videoTrackRef && (this.videoTrackRef.readyState === 'ended' || this.videoTrackRef.muted === true)) {
          console.log('[CamScanner] Focus: detected dead/muted track — closing scanner');
          _clearPermissionFlag();
          if (this.props.onClose) this.props.onClose();
        }
      };
      window.addEventListener('focus', this.handleFocusChange);

      this.trackStateInterval = setInterval(() => {
        if (
          !this._unmounting &&
          !this._restartingAfterBackground &&
          !this._wasBackgroundedWithCamera &&
          this.state.hasPermission === true &&
          this.videoTrackRef &&
          document.visibilityState === 'visible'
        ) {
          const trackDead = this.videoTrackRef.readyState === 'ended' ||
                            this.videoTrackRef.muted === true;
          if (trackDead) {
            console.log('[CamScanner] Polling detected dead track (readyState:', this.videoTrackRef.readyState, ', muted:', this.videoTrackRef.muted, ')');
            _clearPermissionFlag();
            clearInterval(this.trackStateInterval);
            this.trackStateInterval = null;
            if (this.props.onClose) {
              this.props.onClose();
            }
          }
        }
      }, 2000);

      return;
    }

    this.permissionCheckInterval = setInterval(async () => {
      try {
        const { status } = await Camera.getCameraPermissionsAsync();

        if (status !== 'granted' && this.state.hasPermission === true) {
          console.log('Camera permission was revoked');
          this.setState({ hasPermission: false, showPermissionModal: true });

          if (this.props.onClose) {
            this.props.onClose();
          }
        }
      } catch (error) {
        console.error('Error checking camera permission:', error);
      }
    }, 2000);
  };

  stopPermissionMonitoring = () => {
    if (this.permissionStatusRef) {
      this.permissionStatusRef.onchange = null;
      this.permissionStatusRef = null;
    }
    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.handleVisibilityChange = null;
    }
    if (this.handleFocusChange) {
      window.removeEventListener('focus', this.handleFocusChange);
      this.handleFocusChange = null;
    }
    if (this.trackStateInterval) {
      clearInterval(this.trackStateInterval);
      this.trackStateInterval = null;
    }
    if (this.permissionCheckInterval) {
      clearInterval(this.permissionCheckInterval);
      this.permissionCheckInterval = null;
    }
  };

  setStateAsync = (newState) => new Promise(resolve => this.setState(newState, resolve));

  startVideoStream = async (cameraId, existingStream = null) => {
    try {
      if (this._unmounting || !this.cameraRef.current || !this.readerRef) {
        console.warn('[CamScanner] startVideoStream bailing early:',
          'unmounting=', this._unmounting,
          'cameraRef=', !!this.cameraRef.current,
          'readerRef=', !!this.readerRef);
        return;
      }

      let stream;
      if (existingStream) {
        console.log('[CamScanner] Reusing existing stream (skipping getUserMedia)');
        stream = existingStream;
      } else {
        console.log('[CamScanner] Starting video stream for camera:', cameraId);

        const constraints = {
          video: {
            ...(cameraId
              ? { deviceId: { exact: cameraId } }
              : { facingMode: { ideal: 'environment' } }),
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          }
        };

        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      const videoTrack = stream.getVideoTracks()[0];
      this.videoTrackRef = videoTrack;

      videoTrack.onended = () => {
        console.log('[CamScanner] Video track ended (permission revoked or camera released)');
        if (document.visibilityState !== 'visible') {
          console.log('[CamScanner] Tab hidden — deferring close to visibility handler');
          return;
        }
        if (this._wasBackgroundedWithCamera || this._restartingAfterBackground) {
          console.log('[CamScanner] Background return in progress — deferring to visibility handler');
          return;
        }
        if (!this._unmounting && this.state.hasPermission === true && this.props.onClose) {
          _clearPermissionFlag();
          this.props.onClose();
        }
      };

      videoTrack.onmute = () => {
        console.log('[CamScanner] Video track muted — camera feed stopped');
        setTimeout(() => {
          if (
            !this._unmounting &&
            !this._restartingAfterBackground &&
            !this._wasBackgroundedWithCamera &&
            this.state.hasPermission === true &&
            this.videoTrackRef &&
            this.videoTrackRef.muted === true &&
            document.visibilityState === 'visible'
          ) {
            console.log('[CamScanner] Track still muted while page visible — closing scanner');
            _clearPermissionFlag();
            if (this.props.onClose) this.props.onClose();
          }
        }, 1000);
      };

      const capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
      console.log('[CamScanner] Video track capabilities:', capabilities);

      const hasTorch = capabilities.torch === true;
      const hasZoom = capabilities.zoom !== undefined;

      console.log('[CamScanner] - Torch supported:', hasTorch);
      console.log('[CamScanner] - Zoom supported:', hasZoom);
      console.log('[CamScanner] - Zoom range:', hasZoom ? `${capabilities.zoom.min} - ${capabilities.zoom.max}` : 'N/A');

      this.setState({ hasTorch });

      this.cameraRef.current.srcObject = stream;

      await this.cameraRef.current.play();

      if (!this._unmounting) {
        this.setState({ isCameraReady: true });
      }

      // Re-enumerate devices after stream starts — some browsers (iOS Safari) only expose
      // the full camera list once getUserMedia has been called with an active stream.
      if (!this._unmounting) {
        try {
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          if (!this._unmounting) {
            const videoInputs = allDevices.filter(d => d.kind === 'videoinput');
            const hasDeviceLabels = videoInputs.some(d => d.label);
            const refreshedBackCameras = hasDeviceLabels
              ? videoInputs.filter(cam => !(cam.label || '').toLowerCase().includes('front'))
              : videoInputs;
            if (refreshedBackCameras.length > this.state.cameras.length) {
              console.log('[CamScanner] Camera list expanded after stream start:', refreshedBackCameras.length);
              this.setState({ cameras: refreshedBackCameras });
            }
          }
        } catch (e) { /* ignore */ }
      }

      console.log('[CamScanner] Video stream started successfully');
    } catch (error) {
      console.error('[CamScanner] Error starting video stream:', error);
      if (this._unmounting) return;
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        _clearPermissionFlag();
        this.setState({ hasPermission: false, showPermissionModal: true });
      } else if (error.name === 'OverconstrainedError') {
        console.log('[CamScanner] OverconstrainedError, retrying with facingMode fallback');
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } }
          });
          if (this._unmounting) { fallbackStream.getTracks().forEach(t => t.stop()); return; }
          const videoTrack = fallbackStream.getVideoTracks()[0];
          this.videoTrackRef = videoTrack;
          if (this.cameraRef.current) {
            this.cameraRef.current.srcObject = fallbackStream;
            await this.cameraRef.current.play();
            if (!this._unmounting) this.setState({ isCameraReady: true });
          }
        } catch (fallbackErr) {
          console.error('[CamScanner] Fallback also failed:', fallbackErr);
        }
      }
    }
  };

  _selectAndStartCamera = async (backCameras, preferredCameraId, hasLabels, probeStream, probeDeviceId = null) => {
    const stopProbe = () => {
      if (probeStream) probeStream.getTracks().forEach(t => t.stop());
    };

    if (probeStream) {
      if (backCameras.length > 1 && !preferredCameraId && hasLabels) {
        console.log('[CamScanner] Probe path: multiple cameras, no preference — stopping probe, showing camera select modal');
        stopProbe(); // Don't start the camera yet — user needs to choose one first
        this._showCameraSelectModal();
        return;
      }

      const cameraId = probeDeviceId || (backCameras.length > 0 ? backCameras[0].deviceId : '') || '';
      console.log('[CamScanner] Reusing probe stream (avoiding second getUserMedia). cameraId:', cameraId);
      await this.setStateAsync({ selectedCameraId: cameraId });
      if (this._unmounting) { stopProbe(); return; }
      await this.startVideoStream(cameraId, probeStream);
      if (cameraId) {
        AsyncStorage.setItem('preferredCameraId', cameraId).catch(() => {});
      }
      return;
    }

    if (backCameras.length === 0) {
      console.log('[CamScanner] No back cameras found, using facingMode fallback');
      await this.setStateAsync({ selectedCameraId: '' });
      if (this._unmounting) return;
      await this.startVideoStream('');
      return;
    }

    if (backCameras.length === 1) {
      const cameraId = backCameras[0].deviceId || '';
      console.log('[CamScanner] Single back camera, starting:', cameraId);
      await this.setStateAsync({ selectedCameraId: cameraId });
      if (this._unmounting) return;
      await this.startVideoStream(cameraId);
      return;
    }

    if (preferredCameraId) {
      const preferred = backCameras.find(d => d.deviceId === preferredCameraId);
      if (preferred) {
        console.log('[CamScanner] Using preferred camera:', preferred.label);
        await this.setStateAsync({ selectedCameraId: preferred.deviceId });
        if (this._unmounting) return;
        await this.startVideoStream(preferred.deviceId);
        return;
      }
      console.log('[CamScanner] Preferred camera not found, showing selection modal');
    }

    if (hasLabels) {
      console.log('[CamScanner] Multiple cameras, showing selection modal');
      if (!this._unmounting) this._showCameraSelectModal();
    } else {
      console.log('[CamScanner] Multiple cameras but no labels, using facingMode fallback');
      await this.setStateAsync({ selectedCameraId: '' });
      if (this._unmounting) return;
      await this.startVideoStream('');
    }
  };

  initializeCamera = async () => {
    try {
      console.log('[CamScanner] Initializing camera, Platform:', Platform.OS);

      if (Platform.OS !== 'web') {
        const { status } = await Camera.requestCameraPermissionsAsync();
        console.log('[CamScanner] Camera permission status:', status);
        if (status !== 'granted') {
          this.setState({ hasPermission: false, showPermissionModal: true });
          return;
        }
        this.setState({ hasPermission: true });
        return;
      }

      this.readerRef = new BrowserMultiFormatReader();

      const cachedStream = _takeCachedStream();
      if (cachedStream) {
        console.log('[CamScanner] Using cached stream from previous page (zero getUserMedia calls)');
        this.setState({ hasPermission: true });
        _permissionKnownGranted = true;

        const track = cachedStream.getVideoTracks()[0];
        const cachedDeviceId = (track.getSettings ? track.getSettings().deviceId : null) || '';

        const devices = await navigator.mediaDevices.enumerateDevices();
        if (this._unmounting) { cachedStream.getTracks().forEach(t => t.stop()); return; }
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        const hasLabels = videoDevices.some(d => d.label);
        const backCameras = hasLabels
          ? videoDevices.filter(cam => !(cam.label || '').toLowerCase().includes('front'))
          : videoDevices;

        this.setState({ cameras: backCameras, selectedCameraId: cachedDeviceId });
        await this.startVideoStream(cachedDeviceId, cachedStream);

        const cachedPreferred = await AsyncStorage.getItem('preferredCameraId');
        if (!cachedPreferred && backCameras.length > 1 && hasLabels) {
          this._showCameraSelectModal();
        }
        return;
      }

      const preferredCameraId = await AsyncStorage.getItem('preferredCameraId');
      if (this._unmounting) return;
      console.log('[CamScanner] Preferred camera ID:', preferredCameraId);

      let permissionAlreadyGranted = _permissionKnownGranted;
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permStatus = await navigator.permissions.query({ name: 'camera' });
          if (this._unmounting) return;
          if (permStatus.state === 'denied') {
            console.log('[CamScanner] Camera permission already denied (Permissions API)');
            _clearPermissionFlag();
            this.setState({ hasPermission: false, showPermissionModal: true });
            return;
          }
          if (permStatus.state === 'granted') {
            permissionAlreadyGranted = true;
          }
          if (permStatus.state === 'prompt') {
            _clearPermissionFlag();
            permissionAlreadyGranted = false;
          }
        } catch (e) {
          console.log('[CamScanner] Permissions API unavailable, trying enumerateDevices fallback');
          try {
            const devs = await navigator.mediaDevices.enumerateDevices();
            if (this._unmounting) return;
            const hasVideoLabels = devs.some(d => d.kind === 'videoinput' && d.label);
            if (hasVideoLabels) {
              console.log('[CamScanner] enumerateDevices has labels — permission is granted');
              permissionAlreadyGranted = true;
            }
          } catch (e2) { /* enumerateDevices failed — ignore */ }

          if (!permissionAlreadyGranted) {
            try {
              const hint = await AsyncStorage.getItem('cameraPermissionHint');
              if (this._unmounting) return;
              if (hint === 'granted') {
                console.log('[CamScanner] AsyncStorage hint says permission granted');
                permissionAlreadyGranted = true;
              }
            } catch (e3) { /* AsyncStorage read failed — ignore */ }
          }
        }
      } else {
        if (!permissionAlreadyGranted) {
          try {
            const devs = await navigator.mediaDevices.enumerateDevices();
            if (this._unmounting) return;
            const hasVideoLabels = devs.some(d => d.kind === 'videoinput' && d.label);
            if (hasVideoLabels) {
              console.log('[CamScanner] enumerateDevices has labels — permission is granted');
              permissionAlreadyGranted = true;
            }
          } catch (e2) {}
          if (!permissionAlreadyGranted) {
            try {
              const hint = await AsyncStorage.getItem('cameraPermissionHint');
              if (this._unmounting) return;
              if (hint === 'granted') {
                console.log('[CamScanner] AsyncStorage hint says permission granted');
                permissionAlreadyGranted = true;
              }
            } catch (e3) {}
          }
        }
      }

      if (this._unmounting) return;

      if (permissionAlreadyGranted) {
        console.log('[CamScanner] Permission already granted — skipping probe (fast path)');
        this.setState({ hasPermission: true });

        const devices = await navigator.mediaDevices.enumerateDevices();
        if (this._unmounting) return;
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        const hasLabels = videoDevices.some(d => d.label);

        const backCameras = hasLabels
          ? videoDevices.filter(cam => !(cam.label || '').toLowerCase().includes('front'))
          : videoDevices;

        console.log('[CamScanner] Fast path: cameras found:', backCameras.length);
        this.setState({ cameras: backCameras });

        await this._selectAndStartCamera(backCameras, preferredCameraId, hasLabels, null);
        _permissionKnownGranted = true;
        AsyncStorage.setItem('cameraPermissionHint', 'granted').catch(() => {});
        return;
      }

      console.log('[CamScanner] Probe path: requesting getUserMedia for permission');
      let probeStream;
      try {
        probeStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }
        });
      } catch (error) {
        if (this._unmounting) return;
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          console.log('[CamScanner] Browser permission denied');
          this.setState({ hasPermission: false, showPermissionModal: true });
          return;
        }
        throw error;
      }

      if (this._unmounting) {
        probeStream.getTracks().forEach(t => t.stop());
        return;
      }

      this.setState({ hasPermission: true });

      const probeTrack = probeStream.getVideoTracks()[0];
      const probeDeviceId = probeTrack.getSettings ? probeTrack.getSettings().deviceId : null;
      console.log('[CamScanner] Probe stream deviceId:', probeDeviceId);

      const devices = await navigator.mediaDevices.enumerateDevices();
      if (this._unmounting) { probeStream.getTracks().forEach(t => t.stop()); return; }
      const videoDevices = devices.filter(d => d.kind === 'videoinput');

      const backCameras = videoDevices.filter(camera => {
        const label = (camera.label || '').toLowerCase();
        return !label.includes('front');
      });

      console.log('[CamScanner] Probe path: back cameras found:', backCameras.length, backCameras.map(c => c.label));
      this.setState({ cameras: backCameras });

      await this._selectAndStartCamera(backCameras, preferredCameraId, true, probeStream, probeDeviceId);
      _permissionKnownGranted = true;
      AsyncStorage.setItem('cameraPermissionHint', 'granted').catch(() => {});
    } catch (error) {
      console.error('[CamScanner] Error initializing camera:', error);
      if (this.state.hasPermission === null) {
        this.setState({ hasPermission: false, showPermissionModal: true });
      }
    }
  };

  handlePermissionGranted = async () => {
    this.setState({ showPermissionModal: false });
    await this.initializeCamera();
  };

  handlePermissionDenied = () => {
    this.setState({ showPermissionModal: false });
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  _showCameraSelectModal = async () => {
    const tipSeen = await AsyncStorage.getItem('cameraSelectionTipSeen');
    if (!this._unmounting) {
      this.setState({
        showCameraSelectModal: true,
        cameraSelectStep: tipSeen ? 2 : 1,
        cameraSelectShowBack: false,
        isSwitchCameraMode: false,
      });
    }
  };

  handleCameraSelectNext = async () => {
    await AsyncStorage.setItem('cameraSelectionTipSeen', 'true');
    if (!this._unmounting) this.setState({ cameraSelectStep: 2, cameraSelectShowBack: true });
  };

  handleCameraSelectBack = () => {
    if (!this._unmounting) this.setState({ cameraSelectStep: 1 });
  };

  handleCameraSelected = async (cameraId) => {
    await AsyncStorage.setItem('preferredCameraId', cameraId);

    if (cameraId === this.state.selectedCameraId && this.videoTrackRef &&
        this.videoTrackRef.readyState === 'live') {
      console.log('[CamScanner] Selected camera is already active — saving preference only');
      this.setState({ showCameraSelectModal: false, isSwitchCameraMode: false });
      return;
    }

    if (this.videoTrackRef) {
      this.videoTrackRef.onended = null;
      this.videoTrackRef.onmute = null;
      this.videoTrackRef.stop();
      this.videoTrackRef = null;
    }

    await this.setStateAsync({ selectedCameraId: cameraId, showCameraSelectModal: false, isSwitchCameraMode: false, torchEnabled: false, zoom: 1, isCameraReady: false });
    await this.startVideoStream(cameraId);
  };

  handleCameraSelectCancel = () => {
    const { isSwitchCameraMode, cameraSelectStep } = this.state;

    if (isSwitchCameraMode) {
      console.log('[CamScanner] Camera select cancelled in switch mode — keeping scanner open');
      this.setState({ showCameraSelectModal: false, isSwitchCameraMode: false });
      return;
    }

    if (cameraSelectStep === 1) {
      AsyncStorage.setItem('cameraSelectionTipSeen', 'true');
    }

    console.log('[CamScanner] Camera select cancelled — closing scanner');
    if (this.videoTrackRef) {
      this.videoTrackRef.onended = null;
      this.videoTrackRef.onmute = null;
      this.videoTrackRef.stop();
      this.videoTrackRef = null;
    }
    this.setState({ showCameraSelectModal: false });
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  showToast = (message) => {
    this.setState({ toastMessage: message });
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      if (!this._unmounting) this.setState({ toastMessage: null });
    }, 2500);
  };

  handleSwitchCamera = () => {
    const { cameras } = this.state;

    if (!cameras || cameras.length <= 1) {
      console.log('[CamScanner] Only one camera available, cannot switch');
      return;
    }

    console.log('[CamScanner] Switch camera tapped — showing camera selection modal');
    this.setState({
      showCameraSelectModal: true,
      cameraSelectStep: 2,
      cameraSelectShowBack: false,
      isSwitchCameraMode: true,
    });
  };

  handleZoomChange = async (newZoom) => {
    const clampedZoom = Math.min(5, Math.max(1, Math.round(newZoom)));
    console.log('[CamScanner] Zoom changed to (clamped):', clampedZoom);

    if (this.videoTrackRef) {
      try {
        const capabilities = this.videoTrackRef.getCapabilities();
        if (capabilities.zoom) {
          const { min, max } = capabilities.zoom;
          const normalizedZoom = min + ((clampedZoom - 1) / 4) * (max - min);

          console.log('[CamScanner] Applying zoom:', normalizedZoom, '(range:', min, '-', max, ')');

          await this.videoTrackRef.applyConstraints({
            advanced: [{ zoom: normalizedZoom }]
          });

          console.log('[CamScanner] Zoom applied successfully');
        } else {
          console.warn('[CamScanner] Zoom not supported by this camera');
        }
      } catch (error) {
        console.error('[CamScanner] Error applying zoom:', error);
      } finally {
        this.setState({ zoom: clampedZoom });
      }
    } else {
      console.warn('[CamScanner] No video track available for zoom');
      this.setState({ zoom: clampedZoom });
    }

    if (this.state.isScanning) {
      this.scanStartTimeRef = Date.now();
    }
  };

  handleTorchToggle = async () => {
    const newTorchState = !this.state.torchEnabled;
    console.log('[CamScanner] Torch toggle - new state:', newTorchState);

    if (this.videoTrackRef) {
      try {
        const capabilities = this.videoTrackRef.getCapabilities();
        if (capabilities.torch) {
          console.log('[CamScanner] Applying torch constraint:', newTorchState);

          await this.videoTrackRef.applyConstraints({
            advanced: [{ torch: newTorchState }]
          });

          this.setState({ torchEnabled: newTorchState });
          console.log('[CamScanner] Torch toggled successfully');
        } else {
          console.warn('[CamScanner] Torch not supported by this camera');
        }
      } catch (error) {
        console.error('[CamScanner] Error toggling torch:', error);
      }
    } else {
      console.warn('[CamScanner] No video track available for torch');
    }
  };

  handleManualRestart = async () => {
    console.log('[CamScanner] Manual restart triggered by user tap');
    this.setState({ needsManualRestart: false, isCameraReady: false });
    this._restartingAfterBackground = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }
      });
      if (this._unmounting) {
        stream.getTracks().forEach(t => t.stop());
        this._restartingAfterBackground = false;
        return;
      }
      await this.startVideoStream('', stream);
      _permissionKnownGranted = true;
      AsyncStorage.setItem('cameraPermissionHint', 'granted').catch(() => {});
      this._restartingAfterBackground = false;
      console.log('[CamScanner] Camera restarted after manual tap');
    } catch (err) {
      this._restartingAfterBackground = false;
      if (this._unmounting) return;
      console.log('[CamScanner] Manual restart failed:', err.name);
      _clearPermissionFlag();
      if (this.props.onClose) this.props.onClose();
    }
  };

  handleClose = async () => {
    console.log('[CamScanner] Closing camera scanner');

    this.cleanup();

    this.setState({ torchEnabled: false });

    if (Platform.OS !== 'web' && this.cameraRef.current) {
      try {
        await this.cameraRef.current.pausePreview();
        console.log('[CamScanner] Mobile preview paused');
      } catch (err) {
        console.warn('[CamScanner] Error pausing preview:', err);
      }
    }

    if (Platform.OS === 'web' && this.videoTrackRef) {
      try {
        this.videoTrackRef.onended = null;
        this.videoTrackRef.onmute = null;
        this.videoTrackRef.stop();
        this.videoTrackRef = null;
        console.log('[CamScanner] Web video track stopped');
      } catch (err) {
        console.warn('[CamScanner] Error stopping video track:', err);
      }
    }

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleScanAreaTap = () => {
    console.log('[CamScanner] Scan area tapped, isScanning:', this.state.isScanning);
    if (!this.state.isScanning) {
      this.startScanning();
    }
  };

  startScanning = () => {
    console.log('[CamScanner] Starting scanning, Platform:', Platform.OS);
    this.setState({ isScanning: true });
    this.scanStartTimeRef = Date.now();

    if (Platform.OS === 'web') {
      this.startWebScanning();
    } else {
      this.startMobileScanning();
    }
  };

  startWebScanning = () => {
    console.log('[CamScanner] Starting web scanning');
    console.log('[CamScanner] - cameraRef.current:', !!this.cameraRef.current);
    console.log('[CamScanner] - canvasRef.current:', !!this.canvasRef.current);
    console.log('[CamScanner] - readerRef:', !!this.readerRef);

    this.scanIntervalRef = setInterval(async () => {
      try {
        if (Date.now() - this.scanStartTimeRef > SCAN_TIMEOUT_MS) {
          console.log('[CamScanner] Scan timeout reached');
          this.stopScanning();
          if (!_helperShownThisSession) {
            _helperShownThisSession = true;
            this.setState({ showHelperModal: true, helperModalType: 'timeout' });
          }
          return;
        }

        const video = this.cameraRef.current;
        const canvas = this.canvasRef.current;

        if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const luminance = new HTMLCanvasElementLuminanceSource(canvas);
          const bitmap = new BinaryBitmap(new HybridBinarizer(luminance));
          const result = this.readerRef.decodeBitmap(bitmap);
          if (result) {
            console.log('[CamScanner] Barcode detected!');
            const format = result.getBarcodeFormat();
            console.log('[CamScanner] - Format:', format);
            console.log('[CamScanner] - Data:', result.getText());

            if (FORBIDDEN_FORMATS.has(format)) {
              console.log('[CamScanner] 2D barcode detected (forbidden), showing helper modal');
              this.stopScanning();
              this.setState({ showHelperModal: true, helperModalType: '2d' });
              return;
            }

            console.log('[CamScanner] Valid 1D barcode, calling onScan callback');
            this.stopScanning();
            if (this.props.onScan) {
              this.props.onScan(result.getText());
            } else {
              console.warn('[CamScanner] No onScan callback provided!');
            }
          }
        } else {
          if (!video) {
            console.warn('[CamScanner] Video element not available');
          } else if (video.readyState !== video.HAVE_ENOUGH_DATA) {
            console.log('[CamScanner] Video not ready, readyState:', video.readyState);
          }
        }
      } catch (error) {
        if (error.message && !error.message.includes('No MultiFormat Readers')) {
          console.error('[CamScanner] Scanning error:', error.message);
        }
      }
    }, 100);
  };

  startMobileScanning = () => {
    console.log('[CamScanner] Starting mobile scanning (timeout tracking only)');
    this.scanIntervalRef = setInterval(() => {
      if (Date.now() - this.scanStartTimeRef > SCAN_TIMEOUT_MS) {
        console.log('[CamScanner] Mobile scan timeout reached');
        this.stopScanning();
        if (!_helperShownThisSession) {
          _helperShownThisSession = true;
          this.setState({ showHelperModal: true, helperModalType: 'timeout' });
        }
      }
    }, 1000);
  };

  stopScanning = () => {
    console.log('[CamScanner] Stopping scanning');
    this.setState({ isScanning: false });
    if (this.scanIntervalRef) {
      clearInterval(this.scanIntervalRef);
      this.scanIntervalRef = null;
    }
  };

  handleBarCodeScanned = ({ type, data }) => {
    console.log('[CamScanner] Barcode scanned - Type:', type, 'Data:', data);
    console.log('[CamScanner] isScanning state:', this.state.isScanning);

    const barcodeType = type.toUpperCase();
    if (barcodeType.includes('QR') || barcodeType.includes('DATA_MATRIX') ||
      barcodeType.includes('PDF417') || barcodeType.includes('AZTEC')) {
      console.log('[CamScanner] 2D barcode detected, showing helper modal');
      if (!_helperShownThisSession) {
        _helperShownThisSession = true;
        this.setState({ showHelperModal: true, helperModalType: '2d' });
      }
      return;
    }

    console.log('[CamScanner] Valid 1D barcode, calling onScan callback');
    if (this.props.onScan) {
      this.props.onScan(data);
    } else {
      console.warn('[CamScanner] No onScan callback provided!');
    }
  };

  handleHelperModalClose = () => {
    this.setState({ showHelperModal: false, helperModalType: null, helperOpenedFromInfo: false });
  };

  handleInfoPress = () => {
    this.setState({ showHelperModal: true, helperModalType: 'timeout', helperOpenedFromInfo: true });
  };

  renderWebCamera = () => {
    if (this.state.selectedCameraId === null) return null;

    return (
      <>
        <video
          ref={this.cameraRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
            backgroundColor: 'transparent',
            display: 'block',
          }}
          playsInline
          muted
          onLoadedData={() => {
            console.log('[CamScanner] Web video loaded data');
            this.setState({ isCameraReady: true });
          }}
        />
        <canvas
          ref={this.canvasRef}
          style={{ display: 'none' }}
        />
      </>
    );
  };

  renderMobileCamera = () => {
    const normalizedZoom = (this.state.zoom - 1) / 4;

    console.log('[CamScanner] Rendering mobile camera');
    console.log('[CamScanner] - Zoom (1-5):', this.state.zoom);
    console.log('[CamScanner] - Normalized zoom (0-1):', normalizedZoom);
    console.log('[CamScanner] - Torch enabled:', this.state.torchEnabled);
    console.log('[CamScanner] - Flash mode:', this.state.torchEnabled ? 'torch' : 'off');
    console.log('[CamScanner] - Camera type:', this.state.cameraType);

    return (
      <Camera
        ref={this.cameraRef}
        style={styles.camera}
        type={this.state.cameraType}
        flashMode={this.state.torchEnabled ? Camera.Constants.FlashMode.torch : Camera.Constants.FlashMode.off}
        zoom={normalizedZoom}
        onCameraReady={() => {
          console.log('[CamScanner] Mobile camera ready');
          this.setState({ isCameraReady: true });
        }}
        onBarCodeScanned={this.handleBarCodeScanned}
        barCodeScannerSettings={{
          barCodeTypes: [
            'upc_a', 'upc_e', 'ean8', 'ean13',
            'code39', 'code93', 'code128',
            'itf14', 'codabar'
          ],
        }}
        ratio="16:9"
      />
    );
  };

  render() {
    const { hasPermission, showPermissionModal, showCameraSelectModal, cameraSelectStep, showHelperModal, helperModalType, cameras, isScanning, selectedCameraId } = this.state;

    if (hasPermission === null) {
      return <View style={[styles.container, styles.loaderContainer]}><ActivityIndicator size="large" color="#fff" /></View>;
    }

    if (hasPermission === false) {
      return (
        <PermissionModal
          visible={showPermissionModal}
          onAllow={this.handlePermissionGranted}
          onDeny={this.handlePermissionDenied}
          isError={true}
        />
      );
    }

    const hasMultipleCameras = Platform.OS === 'web' ? cameras.length > 1 : false;

    console.log('[CamScanner] Render - Platform:', Platform.OS);
    console.log('[CamScanner] Render - hasMultipleCameras:', hasMultipleCameras);
    console.log('[CamScanner] Render - hasTorch:', this.state.hasTorch);
    console.log('[CamScanner] Render - torchEnabled:', this.state.torchEnabled);
    console.log('[CamScanner] Render - zoom:', this.state.zoom);

    const cameraContent = (
      <View style={[this.props.inline ? styles.inlineContainer : styles.container, this.props.bottomControls && { height: 270 }]}>
        {Platform.OS === 'web' ? this.renderWebCamera() : this.renderMobileCamera()}

        {!this.state.isCameraReady && !this.state.needsManualRestart && (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: '#000', justifyContent: 'center', alignItems: 'center', zIndex: 10 }]}>
            <ActivityIndicator size="large" color="#fff" />
          </View>
        )}

        {this.state.needsManualRestart && (
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={this.handleManualRestart}
            style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center', zIndex: 15 }]}
          >
            <Ionicons name="camera" size={40} color="#fff" style={{ marginBottom: 12 }} />
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: '600', marginBottom: 8 }}>Camera Paused</Text>
            <Text style={{ color: '#ccc', fontSize: 14, textAlign: 'center', paddingHorizontal: 30 }}>
              Tap to restart. Browser may ask for camera permission again.
            </Text>
          </TouchableOpacity>
        )}

        {!this.props.bottomControls && (
          <CameraControls
            torchEnabled={this.state.torchEnabled}
            onTorchToggle={this.handleTorchToggle}
            onSwitchCamera={this.handleSwitchCamera}
            onClose={this.handleClose}
            hasMultipleCameras={hasMultipleCameras}
            hasTorch={this.state.hasTorch}
          />
        )}

        <ScanArea
          isScanning={isScanning}
          onTap={this.handleScanAreaTap}
          bottomControls={this.props.bottomControls}
        />

        <ZoomSlider
          zoom={this.state.zoom}
          onZoomChange={this.handleZoomChange}
          onInfoPress={this.handleInfoPress}
          bottomControls={this.props.bottomControls}
          torchEnabled={this.state.torchEnabled}
          onTorchToggle={this.handleTorchToggle}
          onSwitchCamera={this.handleSwitchCamera}
          onClose={this.handleClose}
          hasMultipleCameras={hasMultipleCameras}
          hasTorch={this.state.hasTorch}
        />

        <CameraSelectModal
          visible={showCameraSelectModal}
          cameras={cameras}
          onSelect={this.handleCameraSelected}
          onCancel={this.handleCameraSelectCancel}
          step={cameraSelectStep}
          onNext={this.handleCameraSelectNext}
          onBack={this.handleCameraSelectBack}
          showBackButton={this.state.cameraSelectShowBack}
          selectedCameraId={selectedCameraId}
        />

        <HelperModal
          visible={showHelperModal}
          type={helperModalType}
          onClose={this.handleHelperModalClose}
          openedFromInfo={this.state.helperOpenedFromInfo}
        />

        {this.state.toastMessage && (
          <View style={{
            position: 'absolute',
            top: 45,
            alignSelf: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            paddingHorizontal: 16,
            paddingVertical: 8,
            borderRadius: 20,
            zIndex: 20,
          }}>
            <Text style={{ color: '#fff', fontSize: 13, fontWeight: '500' }}>
              {this.state.toastMessage}
            </Text>
          </View>
        )}
      </View>
    );

    if (this.props.inline) {
      return cameraContent;
    }

    return (
      <Modal
        visible={true}
        animationType="slide"
        onRequestClose={this.handleClose}
      >
        {cameraContent}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  inlineContainer: {
    width: '100%',
    height: 350,
    backgroundColor: '#000',
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
  },
  camera: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  loaderContainer: {
    top: 5,
  },
});

export default CamScanner;
